import _ from 'underscore';

import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import {WORKLISTS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {
  AddSamplesToTableTrackingParams,
  trackAddSamples,
} from 'notebook/sample-propagation/util/addSamplesTracking';
import {
  sampleFiltersToWorklistTypes,
  worklistTypeToSampleFilter,
} from 'notebook/sample-propagation/util/SampleTypeWorklistTypeConversions';
import {
  StructuredTableAddOperationOptions,
  UnknownStructuredTable,
  UnknownStructuredTableAdapter,
} from 'notebook/structured-tables/StructuredTableConstants';
import {Cell} from 'notebook/tables/constants/CellConstants';
import legacyRunModal from 'ui-library/legacyRunModal';
import {WORKLIST_FILL_LOCATION} from 'worklists/WorklistConstants';

import AddSamplesFromWorklistModal from './AddSamplesFromWorklistModal';
import {dedupeSampleFilters, SampleFilterType} from './SampleFilter';
import getAcceptableSampleFiltersByColumn from './util/getAcceptableSampleFiltersByColumn';
import {makeSampleCellsFromIds} from './util/makeSampleCells';

/**
 * Allows the user to add rows to a table from a worklist. This function handles the entire
 * operation, from opening the "select worklist" modal through to adding the rows to the table.
 */

export default async function addSamplesFromWorklist(
  destinationTable: UnknownStructuredTable,
  adapter: UnknownStructuredTableAdapter,
  addSamples: (samplesByColumn: Array<{samples: Array<Cell>; columnIndex: number}>) => void,
  fillLocation: WORKLIST_FILL_LOCATION,
  options?: StructuredTableAddOperationOptions,
  trackingParams?: AddSamplesToTableTrackingParams
): Promise<void> {
  const convertedTable = adapter.toTable(destinationTable);
  const destinationSampleFiltersByColumn = getAcceptableSampleFiltersByColumn(convertedTable)
    .map((sampleTypeByColumn) => ({
      ...sampleTypeByColumn,
      columns: sampleTypeByColumn.columnIndices.map((index) => destinationTable.columns[index]),
    }))
    .filter(
      ({sampleFilters}) => !sampleFilters.find((sampleFilter) => sampleFilter.type === SampleFilterType.WELL)
    ); // No such thing as a "well" worklist
  const acceptableSampleTypes = dedupeSampleFilters(
    // eslint-disable-next-line no-restricted-syntax
    _.flatten(destinationSampleFiltersByColumn.map(({sampleFilters}) => sampleFilters))
  );
  const acceptableWorklistTypes = sampleFiltersToWorklistTypes(acceptableSampleTypes);
  const addSamplesCommand = await legacyRunModal(AddSamplesFromWorklistModal, {
    worklistTypes: acceptableWorklistTypes,
    destinationSampleFiltersByColumn,
    destinationTable,
    options,
  });

  if (addSamplesCommand) {
    // The modal only sees a filtered subset of Array<StructuredTableColumn>. We need to convert the
    // destination column(s) back into an index into the master `Array<StructuredTableColumn>` array.
    const {worklist, destinationColumnsCids} = addSamplesCommand;

    assert(
      destinationColumnsCids != null,
      'Must provide valid destination column ids when adding samples to table'
    );
    const destinationColumnIndices = destinationColumnsCids.map((cid) =>
      destinationTable.columns.findIndex((column) => column.cid === cid)
    );
    assert(!destinationColumnIndices.includes(-1), 'Sample destination column cid not found', {
      destinationColumnsCids,
    });

    analytics.deprecatedTrackEvent(WORKLISTS_ANALYTICS_ACTIONS.FILLED_FROM_WORKLIST, {
      fill_location: fillLocation,
      worklist_type: worklist.itemType,
      num_items: worklist.itemIds.length,
    });

    const sampleType = worklistTypeToSampleFilter(worklist.itemType);

    assert(
      destinationColumnIndices.length === 1 && sampleType.type !== SampleFilterType.WELL,
      'Adding wells not support from worklists'
    );

    // The cells we create need to match the column type they're being inserted into
    const columnDefinition = convertedTable.columnConstraints[destinationColumnIndices[0]]!.columnDefinition!;
    const newCells = makeSampleCellsFromIds(worklist.itemIds, sampleType.type, columnDefinition);
    assert(
      newCells.length === 1,
      `Must provide exactly 1 column of cells to insert as non-wells, got ${newCells.length} column(s) instead`
    );
    addSamples([{samples: newCells[0].cells, columnIndex: destinationColumnIndices[0]}]);
    if (trackingParams) {
      trackAddSamples({
        ...trackingParams,
        numSamples: newCells[0].cells.length,
        sourceId: worklist.id,
        sourceType: 'worklist',
      });
    }
  }
}
